import { Fragment, FC } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAnglesLeft, faAnglesRight, faAngleLeft, faAngleRight } from '@fortawesome/free-solid-svg-icons';

const Container = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
`;

const PagingButton = styled.button(({ theme }) => {
  const { color, type } = theme;

  return `
    display: flex;
    flex-flow: row nowrap;
    font-size: ${type.size.small};
    border: 1px solid ${color.grayscale[400]};
    padding: 10px;
    align-items: center;
    line-height: 1;

    &.left {
      svg {
        margin-right: 0.5rem;
      }
    }

    &.right {
      svg {
        margin-left: 0.5rem;
      }
    }

    &:not(:first-child) {
      margin-left: 6px;
    }

    &:disabled {
      cursor: auto;
      border: 1px solid ${color.grayscale[800]};
    }

    &:not(:disabled):hover {
      background-color: ${color.grayscale[800]};
    }

    svg {
      height: ${type.size.small};
      display: block;
    }
  `;
});

const getPrev = (currentPage: number) => {
  const prevLinks = [];

  for (let i = currentPage - 2; i < currentPage; i++) {
    if (i > 0) {
      prevLinks.push(i);
    }
  }

  return prevLinks;
};

const getNext = (currentPage: number, totalPages: number) => {
  const nextLinks = [];

  for (let i = currentPage + 1; i <= currentPage + 2; i++) {
    if (i <= totalPages) {
      nextLinks.push(i);
    }
  }

  return nextLinks;
};

export interface IPagination {
  currentPage: number;
  total: number;
  callback: (page: number) => void;
  displayedResults: number;
}

const Pagination: FC<IPagination> = ({ currentPage, total, callback, displayedResults = 10 }) => {
  const totalPages = Math.ceil(total / displayedResults);

  const prevArray = getPrev(currentPage);
  const nextArray = getNext(currentPage, totalPages);

  const prevLoop = prevArray.map((page) => {
    return (
      <PagingButton key={`paging-${page}`} type="button" onClick={() => callback(page)} title={`Page ${page}`}>
        {page}
      </PagingButton>
    );
  });

  const nextLoop = nextArray.map((page) => {
    return (
      <PagingButton key={`paging-${page}`} type="button" onClick={() => callback(page)} title={`Page ${page}`}>
        {page}
      </PagingButton>
    );
  });

  return (
    <Container>
      {totalPages > 1 && (
        <Fragment>
          <PagingButton
            disabled={currentPage <= 1}
            type="button"
            onClick={() => callback(1)}
            title="First page"
            className="left"
          >
            <FontAwesomeIcon icon={faAnglesLeft} /> First
          </PagingButton>

          <PagingButton
            disabled={currentPage <= 1}
            type="button"
            onClick={() => callback(currentPage - 1)}
            title="Previous page"
            className="left"
          >
            <FontAwesomeIcon icon={faAngleLeft} /> Prev
          </PagingButton>

          {prevLoop}

          <PagingButton disabled type="button" title={`Current page, page ${currentPage}`}>
            {currentPage}
          </PagingButton>

          {nextLoop}

          <PagingButton
            disabled={currentPage >= totalPages}
            type="button"
            onClick={() => callback(currentPage + 1)}
            title="Next page"
            className="right"
          >
            Next <FontAwesomeIcon icon={faAngleRight} />
          </PagingButton>

          <PagingButton
            disabled={currentPage >= totalPages}
            type="button"
            onClick={() => callback(totalPages)}
            title="Last page"
            className="right"
          >
            Last <FontAwesomeIcon icon={faAnglesRight} />
          </PagingButton>
        </Fragment>
      )}
    </Container>
  );
};

export default Pagination;
